import PolyParameter from 'parameters/PolyParameter.js';
import {AMP, PITCH, FILTER} from 'constants/envelope-types.js';
import {AudioParamValueParameter} from "parameters/AbstractAudioParamParameter.js"
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';

export default superclass => class extends superclass  {
    setupParameters() {
        this.polyphonyParameter = new IntegerParameter(
            'Polyphony',
            this, (o, x) => {
                // if polyphony is unchanged dont do anything
                if (this.oscillators[0].voices.length === x) {
                    return;
                }
                if (this.algorithmParameter) {
                    this.deleteAlgorithmRouting();
                    this.onChangePolyphony(x);
                    this.onPolyNoise(x);
                    this.clearVoiceData();
                    let oscs = [... this.oscillatorNodes];
                    if (x < oscs[0]._voices.length) {
                        oscs.reverse();
                    }
                    oscs.forEach(
                        osc => osc.polyphony = x);
                    this.currentAlgo = undefined;
                    this.changeAlgorithm(this.algorithmParameter.get(), undefined, undefined, true);
                    this.connectPitchBend();
                }
            },
            {
                documentation: [
                    "The number of voices in the synth.",
                    "When set 1, it becomes monophonic (i.e. only 1 note can be played at a time)"
                ],
                min: 0,
                max: 16,
                unit: '',
                defaultValue: 1,
                automatable: false
            });

        this.registerParameter(this.polyphonyParameter);

        this.algorithmParameter = new IntegerParameter(
            'Algorithm',
            this, (o, algo) => {
                this.changeAlgorithm(algo);
            },
            {
                documentation: [
                    "Choose the configuration for how the operators connect with one another.",
                    "Can get wildly different timbres this way"
                ],
                min: 1,
                max: 8,
                unit: '',
                defaultValue: 1,
                //automatable: false
            });

        this.registerParameter(this.algorithmParameter);

        this.mixParameter = new AudioParamValueParameter(
            'Mix', this.outputCrossFade.fade,
            {
                documentation: "Controls the mix between the operators set to X and Y.",
                min: 0,
                max: 1,
                defaultValue: 0.5,
            });

        this.registerParameter(this.mixParameter);
    }

    getParameters() {
        if (!this.parameters) {
            return [];
        }
        let parameters = [... this.parameters];
        for (let i in this.oscillatorNodes) {
            parameters = parameters.concat(this.oscillatorNodes[i].getParameters());
        }

        parameters = parameters.concat(this.ampEnvelope.parameters)
            .concat(this.pitchEnvelope.parameters)
            .concat(this.hpFilterEnvelope.parameters)
            .concat(this.filterEnvelope.parameters);


        return parameters
            .filter(p => p !== undefined);
    };

    getSubDevices() {
        let subDevices = [
            {value: this.oscillatorNodes[0], label: 'Operator C'},
            {value: this.oscillatorNodes[1], label: 'Operator A'},
            {value: this.oscillatorNodes[2], label: 'Operator B1'},
            {value: this.oscillatorNodes[3], label: 'Operator B2'},
            {value: this, label: 'Mix & Filter'},
            {value: this.ampEnvelope, label: 'Amp Env.'},
            {value: this.oscillatorNodes[1].ampEnvelope, label: 'Mod A Env.'},
            {value: this.oscillatorNodes[2].ampEnvelope, label: 'Mod B1 Env.'},
            {value: this.oscillatorNodes[3].ampEnvelope, label: 'Mod B2 Env.'},
            {value: this.filterEnvelope, label: 'Filter Env.'},
            {value: this.pitchEnvelope, label: 'Pitch Env.'},
            {value: this.hpfilterEnvelope, label: 'HP Filter Env.'},
            // {value: this.oscillatorNodes[0].formants[0], label: 'Formant 1 C.'},
        ];

        for (let osc of this.oscillatorNodes) {
            for (let i=0; i < osc.formants.length; i++) {
                let formant = osc.formants[i];
                subDevices.push({
                    value: formant,
                    label: "Formant " + (i + 1) + " " + osc.name
                });
            }
        }
        return subDevices;
    }

    getSubDeviceIndex(subDevice) {
        return this.getSubDevices()
            .map(x => x.value)
            .indexOf(subDevice);
    }

    getSubDevice(index) {
        let v = this.getSubDevices()[index];
        if (v) {
            return v.value;
        }
        return undefined;
    }

    findParameter(automationJson, name) {
        let envType = automationJson.envelopeType;
        let parameters = this.getParameters();
        if (envType === AMP) {
            parameters = this.ampEnvelope.parameters;
        } else if (envType === PITCH) {
            parameters = this.pitchEnvelope.parameters;
        } else if (envType === FILTER) {
            parameters = this.filterEnvelope.parameters;
        }

        let found = parameters.filter(x => x.name === name);
        if (found.length === 0) {
            return undefined;
        }

        return found[0];
    }

    
    // getters and setters for automatable synth parameters
    get filterCutoff() {
        return this.filterCutoffParameter.get();
    }

    set filterCutoff(cutoff) {
        this.filterCutoffParameter.set(cutoff, true, true);
    }

    get filterQ() {
        return this.filterQParameter.get();
    }

    set filterQ(q) {
        this.filterQParameter.set(q);
    }

    // getters and setters for automatable synth parameters
    get hpFilterCutoff() {
        return this.hpFilterCutoffParameter.get();
    }

    set hpFilterCutoff(cutoff) {
        this.hpFilterCutoffParameter.set(cutoff);
    }

    get hpFilterQ() {
        return this.hpFilterQParameter.get();
    }

    set hpFilterQ(q) {
        this.hpFilterQParameter.set(q);
    }

    isMonophonic() {
        return this.polyphonyParameter.get() === 1;
    }

    // refreshes the synth's parameters to their saved state
    refreshParameters() {
        this.filterQ = this.filterQ;
        this.filterCutoff = this.filterCutoff;
        this.hpFilterCutoff = this.hpFilterCutoff;
    }
}
